import { Link } from "react-router-dom";
import { Phone, Mail, Clock, MapPin, Instagram, Facebook, Youtube, MessageCircle } from "lucide-react";
import Logo from "@/components/Logo";
import { BRAND, BRANCHES, waLink, NAV_LINKS, ACTIVITIES, CATEGORIES } from "@/data/site";

const Footer = () => (
  <footer className="border-t border-white/10 bg-black" data-testid="main-footer">
    <div className="mx-auto max-w-[1440px] px-5 py-16 sm:px-8">
      <div className="mb-14 flex flex-col items-start justify-between gap-8 border-b border-white/10 pb-12 lg:flex-row lg:items-center">
        <div>
          <p className="font-mono2 text-[11px] uppercase tracking-[0.32em] text-red-500">Ready to begin your journey?</p>
          <h2 className="mt-3 font-display text-3xl font-black uppercase tracking-tight sm:text-4xl">
            30+ Years. One Stage. <span className="text-red-500">Your Moment.</span>
          </h2>
        </div>
        <Link
          to="/booking?mode=enquiry"
          className="bg-gradient-to-r from-red-600 via-rose-600 to-red-700 px-8 py-4 font-display text-xs font-extrabold uppercase tracking-[0.18em] text-white shadow-[0_0_24px_rgba(220,38,38,0.35)] transition-all hover:shadow-[0_0_38px_rgba(220,38,38,0.55)]"
          data-testid="footer-enquire-btn"
        >
          Enquire Now
        </Link>
      </div>

      <div className="grid grid-cols-1 gap-12 sm:grid-cols-2 lg:grid-cols-4">
        <div>
          <Logo />
          <p className="mt-5 text-sm leading-relaxed text-zinc-400">
            Where talent finds direction and passion becomes performance.
          </p>
          <p className="mt-3 font-mono2 text-[11px] uppercase tracking-[0.22em] text-zinc-500">
            30+ Years of Experience
          </p>
          <div className="mt-6 flex gap-3">
            {[
              { icon: Instagram, href: BRAND.instagram1, label: "Instagram — @anilraj0072", id: "instagram-1" },
              { icon: Instagram, href: BRAND.instagram2, label: "Instagram — @newboogiewoogieacademy1", id: "instagram-2" },
              { icon: Facebook, href: BRAND.facebook, label: "Facebook", id: "facebook" },
              { icon: Youtube, href: BRAND.youtube, label: "YouTube", id: "youtube" },
            ].map((s) => (
              <a
                key={s.id}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={s.label}
                className="flex h-10 w-10 items-center justify-center border border-white/15 text-zinc-400 transition-all hover:border-red-500 hover:text-red-400"
                data-testid={`footer-social-${s.id}`}
              >
                <s.icon size={16} />
              </a>
            ))}
          </div>
        </div>

        <nav aria-label="Quick links">
          <h3 className="font-mono2 text-[11px] uppercase tracking-[0.28em] text-zinc-500">Quick Links</h3>
          <ul className="mt-5 grid grid-cols-2 gap-x-4 gap-y-2.5">
            {NAV_LINKS.map((l) => (
              <li key={l.path}>
                <Link to={l.path} className="text-sm text-zinc-400 transition-colors hover:text-red-400" data-testid={`footer-link-${l.name.toLowerCase()}`}>
                  {l.name}
                </Link>
              </li>
            ))}
            <li>
              <a href={BRANCHES[0].maps} target="_blank" rel="noopener noreferrer" className="text-sm text-zinc-400 transition-colors hover:text-red-400" data-testid="footer-link-reviews-google">
                Google Reviews
              </a>
            </li>
          </ul>
        </nav>

        <nav aria-label="Activities">
          <h3 className="font-mono2 text-[11px] uppercase tracking-[0.28em] text-zinc-500">Activities</h3>
          <ul className="mt-5 space-y-2.5">
            {CATEGORIES.map((c) => (
              <li key={c}>
                <Link to={`/activities?cat=${encodeURIComponent(c)}`} className="text-sm font-semibold text-zinc-300 transition-colors hover:text-red-400">
                  {c}
                </Link>
              </li>
            ))}
            {ACTIVITIES.slice(0, 6).map((a) => (
              <li key={a.slug}>
                <Link to={`/activities?cat=${encodeURIComponent(a.cat)}`} className="text-sm text-zinc-500 transition-colors hover:text-red-400">
                  {a.name}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <div>
          <h3 className="font-mono2 text-[11px] uppercase tracking-[0.28em] text-zinc-500">Contact</h3>
          <ul className="mt-5 space-y-3.5 text-sm text-zinc-400">
            <li>
              <a href={BRAND.phoneLink} className="flex items-center gap-3 transition-colors hover:text-red-400" data-testid="footer-phone">
                <Phone size={15} className="shrink-0 text-red-500" /> {BRAND.phoneDisplay}
              </a>
            </li>
            <li>
              <a href={waLink()} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 transition-colors hover:text-red-400" data-testid="footer-whatsapp">
                <MessageCircle size={15} className="shrink-0 text-red-500" /> {BRAND.whatsappDisplay} (WhatsApp)
              </a>
            </li>
            <li>
              <a href={`mailto:${BRAND.email}`} className="flex items-center gap-3 break-all transition-colors hover:text-red-400" data-testid="footer-email">
                <Mail size={15} className="shrink-0 text-red-500" /> {BRAND.email}
              </a>
            </li>
            <li className="flex items-start gap-3">
              <Clock size={15} className="mt-0.5 shrink-0 text-red-500" /> {BRAND.hours}
            </li>
            {BRANCHES.map((c) => (
              <li key={c.id}>
                <a href={c.maps} target="_blank" rel="noopener noreferrer" className="flex items-start gap-3 transition-colors hover:text-red-400" data-testid={`footer-map-${c.id}`}>
                  <MapPin size={15} className="mt-0.5 shrink-0 text-red-500" />
                  <span>
                    <span className="block font-semibold text-zinc-300">{c.name}</span>
                    <span className="block text-xs text-zinc-500">{c.address}</span>
                  </span>
                </a>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="mt-14 flex flex-col items-center justify-between gap-4 border-t border-white/10 pt-8 sm:flex-row">
        <p className="text-xs text-zinc-500">© 2026 New Boogie Woogie Academy. All Rights Reserved.</p>
        <p className="font-mono2 text-[10px] uppercase tracking-[0.24em] text-zinc-600">
          Discipline · Training · Creativity · Confidence · Performance
        </p>
      </div>
    </div>
  </footer>
);

export default Footer;
